import React, {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { Popover, ThemeProvider } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'

import { getTheme } from 'themes'
import { BOARD } from 'components_new/molecules/Node/accents'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { ActiveSource, SourceTable } from 'types/GlobalDataset'

interface SourceTableSelectProps {
  onChange: (sourceTableId: string) => void
  sources: ActiveSource[]
  value: string | null
}

type Match = {
  source: ActiveSource
  table: SourceTable
}

/** A search hit split around the matched substring, for highlighting. */
type FlatRow = Match & {
  pre: string
  hit: string
  post: string
  index: number
}

/**
 * Green "table chosen" treatment for the trigger field. Hexes are fixed on
 * purpose (see accents.ts) so a customer's profile colors can't recolor them.
 */
const CHOSEN = {
  border: BOARD.signalGreen,
  ring: '#DCFCE7',
  tileBg: '#BBF7D0',
  tileFg: '#166534',
  hitFg: BOARD.signalGreenDark,
  hitBg: '#DCFCE7'
} as const

/**
 * At ~2 000 tables across all sources a full list is unusable, so search
 * results are capped and the user is nudged to refine the query.
 */
const MAX_ROWS = 50

const tableCount = (source: ActiveSource) => Object.keys(source.tables).length

/**
 * Source-table picker for the transformation Input node.
 *
 * Selection is search-first, built for catalogues of thousands of tables across
 * many sources. The popover has three states:
 *   1. Empty query, no scope  → browse the sources, each with its table count.
 *   2. Scoped to one source   → a back chip + search runs within that source.
 *   3. Any query typed        → a flat, relevance-ranked list across the pool,
 *                               with the matched substring highlighted and the
 *                               source shown beneath each table.
 * Picking a table turns the field green so the chosen table and source are
 * always obvious. Keyboard: ↑/↓ move, Enter selects/scopes, Esc closes.
 */
const SourceTableSelect = (props: SourceTableSelectProps) => {
  const { onChange, sources, value } = props

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [scopeId, setScopeId] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const listRef = useRef<HTMLDivElement | null>(null)

  const open = Boolean(anchorEl)
  const query = searchQuery.trim().toLowerCase()

  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const selected = useMemo((): Match | null => {
    if (!value) {
      return null
    }

    for (const source of sources) {
      const table = source.tables[value]

      if (table) {
        return { source, table }
      }
    }

    return null
  }, [sources, value])

  const scopeSource = useMemo(
    () => sources.find((source) => source.value === scopeId) ?? null,
    [sources, scopeId]
  )

  /** Sources to browse when there is no query and no scope. */
  const browseSources = useMemo(
    () =>
      [...sources]
        .filter((source) => tableCount(source) > 0)
        .sort((a, b) => sortAlphabeticalAsc(a.label, b.label)),
    [sources]
  )

  /**
   * Flat, ranked matches across the current pool. Ranking floats start-of-name
   * and tighter matches up: (1) earliest match index, (2) shorter name,
   * (3) alphabetical.
   */
  const flat = useMemo(() => {
    if (!query && !scopeSource) {
      return null
    }

    const pool = scopeSource ? [scopeSource] : sources
    const hits: FlatRow[] = []

    for (const source of pool) {
      for (const table of Object.values(source.tables)) {
        const index = query ? table.label.toLowerCase().indexOf(query) : 0

        if (index >= 0) {
          hits.push({
            source,
            table,
            index,
            pre: query ? table.label.slice(0, index) : table.label,
            hit: query ? table.label.slice(index, index + query.length) : '',
            post: query ? table.label.slice(index + query.length) : ''
          })
        }
      }
    }

    hits.sort(
      (a, b) =>
        a.index - b.index ||
        a.table.label.length - b.table.label.length ||
        sortAlphabeticalAsc(a.table.label, b.table.label)
    )

    return { total: hits.length, rows: hits.slice(0, MAX_ROWS) }
  }, [sources, scopeSource, query])

  const showBrowse = !query && !scopeSource
  const rowCount = showBrowse ? browseSources.length : flat?.rows.length ?? 0

  // Keep the keyboard cursor in range as the visible rows change.
  useEffect(() => {
    setActiveIndex(0)
  }, [query, scopeId, open])

  // Scroll the highlighted row into view during keyboard navigation.
  useEffect(() => {
    const container = listRef.current

    if (!container) {
      return
    }

    const row = container.querySelector<HTMLElement>(
      `[data-row-index="${activeIndex}"]`
    )

    row?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSearchQuery('')
    setScopeId(null)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSearchQuery('')
    setScopeId(null)
  }

  const handleScope = (sourceId: string) => {
    setScopeId(sourceId)
    setSearchQuery('')
  }

  const handleUnscope = () => {
    setScopeId(null)
    setSearchQuery('')
  }

  const handlePick = (sourceTableId: string) => {
    onChange(sourceTableId)
    handleClose()
  }

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, rowCount - 1))
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
      } else if (event.key === 'Enter') {
        event.preventDefault()

        if (showBrowse) {
          const source = browseSources[activeIndex]

          if (source) {
            handleScope(source.value)
          }
        } else {
          const row = flat?.rows[activeIndex]

          if (row) {
            handlePick(row.table.value)
          }
        }
      } else if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    },
    [rowCount, showBrowse, browseSources, flat, activeIndex]
  )

  const isChosen = Boolean(selected)

  const searchPlaceholder = scopeSource
    ? `Sök i ${scopeSource.label}…`
    : 'Sök tabell i alla källor…'

  const hint = scopeSource
    ? `${flat?.total ?? 0} tabeller i ${scopeSource.label}${
      query ? ` · "${searchQuery.trim()}"` : ''
    }`
    : query
      ? `${flat?.total ?? 0} träffar i alla källor`
      : ''

  const more = flat ? Math.max(0, flat.total - MAX_ROWS) : 0

  return (
    <>
      <Box
        className="nodrag ignore-locked-mode"
        onClick={handleOpen}
        sx={{
          alignItems: 'center',
          backgroundColor: 'common.white',
          border: `1px solid ${BOARD.border}`,
          borderRadius: '11px',
          boxShadow: BOARD.shadowRest,
          cursor: 'pointer',
          display: 'flex',
          gap: 1.25,
          p: '9px 11px',
          transition:
            'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
          '&:hover': {
            backgroundColor: '#F9F9FB',
            borderColor: '#D4D4D8'
          }
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: isChosen ? CHOSEN.tileBg : BOARD.chipBackground,
            borderRadius: '8px',
            color: isChosen ? CHOSEN.tileFg : BOARD.textFaint,
            display: 'flex',
            flex: 'none',
            height: 30,
            justifyContent: 'center',
            width: 30
          }}
        >
          <Icon name="Table" sx={{ fontSize: '16px' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              color: isChosen ? BOARD.textPrimary : BOARD.textFaint,
              fontFamily: BOARD.fontBody,
              fontSize: '13.5px',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {selected ? selected.table.label : 'Välj tabell'}
          </Box>
          <Box
            sx={{
              color: BOARD.textFaint,
              fontFamily: BOARD.fontBody,
              fontSize: '11.5px',
              mt: '1px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {selected
              ? selected.source.label
              : 'Klicka för att söka bland källor'}
          </Box>
        </Box>
        <Icon
          name="ExpandMore"
          sx={{ color: BOARD.textFaint, flex: 'none', fontSize: '16px' }}
        />
      </Box>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        disableScrollLock={true}
        onClose={handleClose}
        onMouseDown={(event) => event.stopPropagation()}
        open={open}
        transitionDuration={reduceMotion ? 0 : undefined}
        slotProps={{
          paper: {
            sx: {
              border: `1px solid ${BOARD.border}`,
              borderRadius: '13px',
              boxShadow: '0 14px 34px rgba(16, 24, 40, 0.16)',
              mt: '6px',
              overflow: 'hidden',
              width: 320
            }
          }
        }}
      >
        <ThemeProvider theme={getTheme('light')}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', maxHeight: 360 }}
          >
            <Box
              sx={{
                borderBottom: `1px solid ${BOARD.borderSoft}`,
                p: '10px 11px 8px'
              }}
            >
              {scopeSource ? (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 0.75,
                    mb: '7px'
                  }}
                >
                  <Box
                    role="button"
                    onClick={handleUnscope}
                    sx={{
                      alignItems: 'center',
                      backgroundColor: BOARD.chipBackground,
                      borderRadius: '7px',
                      color: BOARD.textSecondary,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      fontFamily: BOARD.fontBody,
                      fontSize: '11.5px',
                      fontWeight: 600,
                      gap: '4px',
                      p: '4px 9px 4px 6px',
                      '&:hover': { backgroundColor: '#EDEDF0' }
                    }}
                  >
                    <Icon name="ChevronLeft" sx={{ fontSize: '15px' }} />
                    Alla källor
                  </Box>
                  <Box
                    sx={{
                      color: BOARD.textPrimary,
                      fontFamily: BOARD.fontBody,
                      fontSize: '11.5px',
                      fontWeight: 600,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {scopeSource.label}
                  </Box>
                </Box>
              ) : null}
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: BOARD.chipBackground,
                  borderRadius: '9px',
                  display: 'flex',
                  gap: 1,
                  p: '7px 10px'
                }}
              >
                <Icon
                  name="Search"
                  sx={{ color: BOARD.textFaint, fontSize: '16px' }}
                />
                <input
                  autoFocus={true}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: BOARD.textPrimary,
                    flex: 1,
                    fontFamily: BOARD.fontBody,
                    fontSize: '13px',
                    minWidth: 0,
                    outline: 'none',
                    padding: 0
                  }}
                />
              </Box>
            </Box>

            <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
              {hint ? (
                <Box
                  sx={{
                    color: BOARD.textFaint,
                    fontFamily: BOARD.fontBody,
                    fontSize: '11px',
                    p: '8px 13px 3px'
                  }}
                >
                  {hint}
                </Box>
              ) : null}

              {showBrowse ? (
                <>
                  <Box
                    sx={{
                      color: BOARD.textFaint,
                      fontFamily: BOARD.fontDisplay,
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      p: '8px 13px 5px',
                      textTransform: 'uppercase'
                    }}
                  >
                    Bläddra per källa
                  </Box>
                  {browseSources.map((source, rowIndex) => (
                    <Box
                      data-row-index={rowIndex}
                      key={source.value}
                      onClick={() => handleScope(source.value)}
                      onMouseEnter={() => setActiveIndex(rowIndex)}
                      sx={{
                        alignItems: 'center',
                        backgroundColor:
                          activeIndex === rowIndex
                            ? BOARD.chipBackground
                            : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 1.25,
                        p: '8px 13px'
                      }}
                    >
                      <Box
                        sx={{
                          alignItems: 'center',
                          backgroundColor: BOARD.chipBackground,
                          borderRadius: '7px',
                          color: BOARD.textMuted,
                          display: 'flex',
                          flex: 'none',
                          height: 26,
                          justifyContent: 'center',
                          width: 26
                        }}
                      >
                        <Icon
                          name="StorageOutlined"
                          sx={{ fontSize: '15px' }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            color: BOARD.textPrimary,
                            fontFamily: BOARD.fontBody,
                            fontSize: '13px',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {source.label}
                        </Box>
                        <Box
                          sx={{
                            color: BOARD.textFaint,
                            fontFamily: BOARD.fontBody,
                            fontSize: '11px'
                          }}
                        >
                          {tableCount(source)} tabeller
                        </Box>
                      </Box>
                      <Icon
                        name="ChevronRight"
                        sx={{
                          color: '#C4C4CC',
                          flex: 'none',
                          fontSize: '16px'
                        }}
                      />
                    </Box>
                  ))}
                </>
              ) : null}

              {!showBrowse && flat ? (
                <>
                  {flat.rows.map((row, rowIndex) => (
                    <Box
                      data-row-index={rowIndex}
                      key={row.table.value}
                      onClick={() => handlePick(row.table.value)}
                      onMouseEnter={() => setActiveIndex(rowIndex)}
                      sx={{
                        alignItems: 'center',
                        backgroundColor:
                          activeIndex === rowIndex
                            ? BOARD.chipBackground
                            : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 1.25,
                        p: '7px 13px'
                      }}
                    >
                      <Box
                        sx={{
                          alignItems: 'center',
                          backgroundColor: BOARD.chipBackground,
                          borderRadius: '7px',
                          color: BOARD.textMuted,
                          display: 'flex',
                          flex: 'none',
                          height: 24,
                          justifyContent: 'center',
                          width: 24
                        }}
                      >
                        <Icon name="Table" sx={{ fontSize: '14px' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            color: BOARD.textPrimary,
                            fontFamily: BOARD.fontBody,
                            fontSize: '13px',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {row.pre}
                          {row.hit ? (
                            <Box
                              component="span"
                              sx={{
                                backgroundColor: CHOSEN.hitBg,
                                borderRadius: '3px',
                                color: CHOSEN.hitFg,
                                fontWeight: 700,
                                px: '1px'
                              }}
                            >
                              {row.hit}
                            </Box>
                          ) : null}
                          {row.post}
                        </Box>
                        <Box
                          sx={{
                            color: BOARD.textFaint,
                            fontFamily: BOARD.fontBody,
                            fontSize: '11px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {row.source.label}
                        </Box>
                      </Box>
                      {row.table.value === value ? (
                        <Icon
                          name="Check"
                          sx={{
                            color: BOARD.signalGreen,
                            flex: 'none',
                            fontSize: '16px'
                          }}
                        />
                      ) : null}
                    </Box>
                  ))}
                  {more > 0 ? (
                    <Box
                      sx={{
                        color: BOARD.textFaint,
                        fontFamily: BOARD.fontBody,
                        fontSize: '11.5px',
                        p: '6px 13px 8px'
                      }}
                    >
                      +{more} fler — förfina sökningen
                    </Box>
                  ) : null}
                  {flat.total === 0 ? (
                    <Box
                      sx={{
                        color: BOARD.textFaint,
                        fontFamily: BOARD.fontBody,
                        fontSize: '12.5px',
                        p: '16px 8px',
                        textAlign: 'center'
                      }}
                    >
                      Inga träffar
                    </Box>
                  ) : null}
                </>
              ) : null}
            </Box>
          </Box>
        </ThemeProvider>
      </Popover>
    </>
  )
}

export default SourceTableSelect
